import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';

import { Clinic, getClinic } from '@/data/Clinics';
import { DentistHour, getDentistHoursByDentistId } from '@/data/DentistHours';
import { Dentist, getDentistsByClinicId } from '@/data/Dentists';
import {
  Patient,
  createPatient,
  getPatient,
  updatePatient
} from '@/data/Patients';
import {
  Request,
  createRequest,
  getRequestsByDentistIdAndStatus
} from '@/data/Requests';
import {
  Service,
  getDefaultServices,
  getServicesByDentistId
} from '@/data/Services';
import useToast from '@/hooks/useToast';
import {
  DEFAULT_DURATION_IN_MINUTES,
  MAXIMUM_DATE_RANGE_IN_MONTHS,
  MAXIMUM_DURATION_IN_MINUTES,
  OVERLAP_TIME_REDUCTION_IN_MINUTES,
  PURPOSE_JOIN_SEPARATOR,
  REQUEST_STATUS_OPTIONS,
  SCHEDULE_FLOW_STEPS,
  SELECT_MODAL_OTHERS,
  TIMESLOT_BUFFER_IN_HOURS
} from '@/utils/constants';
import { formatDentistName } from '@/utils/helpers/formatDentistName';
import { formatMobileNumber } from '@/utils/helpers/formatMobileNumber';
import { formatName } from '@/utils/helpers/formatName';
import { generateUid, isValidUid } from '@/utils/helpers/uid';
import { supabase } from '@/utils/supabase';
import { Gender } from '@/utils/types';
import moment from 'moment';
import { usePostHog } from 'posthog-js/react';
import { useParams } from 'react-router';

interface ScheduleFlowContextProps {
  loading: boolean;
  clinic: Clinic | null;
  dentists: Dentist[];
  selectedDentist: Dentist | null;
  setSelectedDentist: React.Dispatch<React.SetStateAction<Dentist | null>>;
  availableDates: Date[];
  setAvailableDates: React.Dispatch<React.SetStateAction<Date[]>>;
  availableTimeslots: string[];
  selectedDate: Date | null;
  setSelectedDate: React.Dispatch<React.SetStateAction<Date | null>>;
  selectedTime: string | null;
  setSelectedTime: React.Dispatch<React.SetStateAction<string | null>>;
  purpose: string | null;
  setPurpose: React.Dispatch<React.SetStateAction<string | null>>;
  othersPurpose: string | null;
  setOthersPurpose: React.Dispatch<React.SetStateAction<string | null>>;
  currentStep: string;
  setCurrentStep: React.Dispatch<React.SetStateAction<string>>;
  patient: Patient | null;
  patientId: string | null;
  firstName: string | null;
  lastName: string | null;
  mobileNumber: string | null;
  gender: Gender | null;
  birthday: string | null;
  setFirstName: React.Dispatch<React.SetStateAction<string | null>>;
  setLastName: React.Dispatch<React.SetStateAction<string | null>>;
  setMobileNumber: React.Dispatch<React.SetStateAction<string | null>>;
  setGender: React.Dispatch<React.SetStateAction<Gender | null>>;
  setBirthday: React.Dispatch<React.SetStateAction<string | null>>;
  durationInMinutes: number;
  services: Service[];
  fetchServices: () => Promise<void>;
  fetchSelectedDentistSchedule: () => Promise<void>;
  sendOtp: () => Promise<void>;
  resendOtp: () => Promise<void>;
  verifyOtp: (otp: string) => Promise<void>;
  request: Request | null;
  confirmRequest: () => Promise<void>;
  reset: () => void;
}

const ScheduleFlowContext = createContext<ScheduleFlowContextProps | undefined>(
  undefined
);

interface ScheduleFlowProviderProps {
  children: React.ReactNode;
}

export const ScheduleFlowProvider: React.FC<ScheduleFlowProviderProps> = ({
  children
}) => {
  const posthog = usePostHog();
  const displayToast = useToast();
  const { id: clinicId } = useParams<{ id: string }>();

  const [loading, setLoading] = useState<boolean>(true);
  const [clinic, setClinic] = useState<Clinic | null>(null);
  const [dentists, setDentists] = useState<Dentist[]>([]);
  const [selectedDentist, setSelectedDentist] = useState<Dentist | null>(null);
  const [dentistHoursByDay, setDentistHoursByDay] = useState<{
    [key: number]: DentistHour | null;
  } | null>(null);
  const [requests, setRequests] = useState<Request[]>([]);
  const [availableDates, setAvailableDates] = useState<Date[]>([]);
  const [availableTimeslots, setAvailableTimeslots] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [purpose, setPurpose] = useState<string | null>(null);
  const [othersPurpose, setOthersPurpose] = useState<string | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [currentStep, setCurrentStep] = useState<string>(
    SCHEDULE_FLOW_STEPS.SELECT_DENTIST
  );
  const [patientId, setPatientId] = useState<string | null>(null);
  const [firstName, setFirstName] = useState<string | null>(null);
  const [lastName, setLastName] = useState<string | null>(null);
  const [mobileNumber, setMobileNumber] = useState<string | null>(null);
  const [gender, setGender] = useState<Gender | null>(null);
  const [birthday, setBirthday] = useState<string | null>(null);
  const [authenticated, setAuthenticated] = useState<boolean>(false);
  const [authenticatedPatientId, setAuthenticatedPatientId] = useState<
    string | null
  >(null);
  const [authenticatedMobileNumber, setAuthenticatedMobileNumber] = useState<
    string | null
  >(null);

  const durationInMinutes: number = useMemo(() => {
    if (purpose && services.length > 0) {
      const calculatedDuration = purpose
        .split(PURPOSE_JOIN_SEPARATOR)
        .sort((a, b) => {
          const durationA =
            services.find((option) => option.name === a)?.duration ||
            DEFAULT_DURATION_IN_MINUTES;
          const durationB =
            services.find((option) => option.name === b)?.duration ||
            DEFAULT_DURATION_IN_MINUTES;
          return durationB - durationA;
        })
        .reduce(
          (total, purpose, index, purposes) =>
            total +
            (services.find((option) => option.name === purpose)?.duration ||
              DEFAULT_DURATION_IN_MINUTES) -
            (purposes.length > 1 && index !== 0
              ? OVERLAP_TIME_REDUCTION_IN_MINUTES
              : 0),
          0
        );

      return Math.min(calculatedDuration, MAXIMUM_DURATION_IN_MINUTES);
    }

    return DEFAULT_DURATION_IN_MINUTES;
  }, [services.length, purpose]);

  const patient: Patient = useMemo(
    () => ({
      first_name: formatName(firstName),
      last_name: formatName(lastName),
      mobile_number: mobileNumber,
      gender,
      birthday
    }),
    [firstName, lastName, mobileNumber, gender, birthday]
  );

  const request: Request = useMemo(
    () => ({
      date: selectedDate ? moment(selectedDate).format('YYYY-MM-DD') : null,
      start_time: selectedTime,
      end_time: selectedTime
        ? moment(selectedTime, 'HH:mm')
            .add(durationInMinutes, 'minutes')
            .format('HH:mm')
        : null,
      dentist_id: selectedDentist?.id!,
      status: REQUEST_STATUS_OPTIONS.PENDING,
      purpose:
        purpose === SELECT_MODAL_OTHERS &&
        othersPurpose &&
        othersPurpose.trim() !== ''
          ? `${purpose}: ${othersPurpose}`
          : purpose,
      patient_id: patientId
    }),
    [
      selectedDate,
      selectedTime,
      durationInMinutes,
      selectedDentist,
      purpose,
      patientId
    ]
  );

  const fetchClinicAndDentists = async () => {
    if (!isValidUid(clinicId)) {
      displayToast({
        message:
          'Invalid clinic ID. Please double check the URL and try again.',
        duration: 5000,
        position: 'top',
        positionAnchor: 'logo'
      });
      return;
    }

    try {
      setLoading(true);
      const [clinic, dentists] = await Promise.all([
        getClinic(clinicId),
        getDentistsByClinicId(clinicId)
      ]);

      setClinic(clinic);
      setDentists(
        dentists.sort((a, b) => a.first_name.localeCompare(b.first_name))
      );
      if (dentists.length > 0 && !selectedDentist) {
        setSelectedDentist(dentists[0]);
      } else if (dentists.length === 0) {
        displayToast({
          message: `No dentists found. Please contact support.`,
          duration: 5000,
          position: 'top',
          positionAnchor: 'logo'
        });
      }
    } catch (error: any) {
      displayToast({
        message: `Error fetching dentists: ${error.message}. Please contact support.`,
        duration: 5000,
        position: 'top',
        positionAnchor: 'logo'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    setLoading(true);
    try {
      if (selectedDentist?.id) {
        const options = await getServicesByDentistId(selectedDentist.id);
        if (options.length > 0) {
          setServices(options);
        } else {
          const defaultOptions = await getDefaultServices();
          setServices(defaultOptions);
        }
        setCurrentStep(SCHEDULE_FLOW_STEPS.SELECT_PURPOSE);
      }
    } catch (error: any) {
      displayToast({
        message: `Error fetching purpose options: ${error.message}. Please contact support.`,
        duration: 5000,
        position: 'top',
        positionAnchor: 'logo'
      });
    } finally {
      setLoading(false);
    }
  };

  const getTimeslotsForDate = useCallback(
    (date: Date) => {
      if (!selectedDentist || !dentistHoursByDay) {
        return [];
      }

      const dayOfWeek = moment(date).day();
      const dentistDayHours = dentistHoursByDay[dayOfWeek];

      if (!dentistDayHours) {
        return [];
      }

      const startTime = moment(
        `${moment(date).format('YYYY-MM-DD')} ${dentistDayHours.start_time}`,
        'YYYY-MM-DD HH:mm'
      );
      const endTime = moment(
        `${moment(date).format('YYYY-MM-DD')} ${dentistDayHours.end_time}`,
        'YYYY-MM-DD HH:mm'
      );
      const timeslots: string[] = [];

      let currentTime = startTime.clone();
      const bufferTime = moment().add(TIMESLOT_BUFFER_IN_HOURS, 'hours');

      const isSlotBlocked = (
        slotStartTime: moment.Moment,
        slotEndTime: moment.Moment
      ) => {
        return requests
          .filter((request) =>
            moment(date).isSame(moment(request.date, 'YYYY-MM-DD'), 'day')
          )
          .find((request) => {
            const requestStartTime = moment(
              `${request.date} ${request.start_time}`,
              'YYYY-MM-DD HH:mm'
            );
            const requestEndTime = moment(
              `${request.date} ${request.end_time}`,
              'YYYY-MM-DD HH:mm'
            );

            return (
              slotStartTime.isBetween(
                requestStartTime,
                requestEndTime,
                null,
                '[)'
              ) ||
              slotEndTime.isBetween(
                requestStartTime,
                requestEndTime,
                null,
                '(]'
              ) ||
              requestStartTime.isBetween(
                slotStartTime,
                slotEndTime,
                null,
                '[)'
              ) ||
              requestEndTime.isBetween(slotStartTime, slotEndTime, null, '(]')
            );
          });
      };

      while (currentTime.isBefore(endTime)) {
        const slotEndTime = currentTime
          .clone()
          .add(durationInMinutes, 'minutes');

        const conflictingRequest = isSlotBlocked(currentTime, slotEndTime);

        if (
          !conflictingRequest &&
          currentTime.isAfter(bufferTime) &&
          slotEndTime.isSameOrBefore(endTime)
        ) {
          timeslots.push(currentTime.format('HH:mm'));
        } else {
          if (conflictingRequest) {
            currentTime = moment(
              `${conflictingRequest.date} ${conflictingRequest.end_time}`,
              'YYYY-MM-DD HH:mm'
            );
            continue;
          }
        }

        const remainder =
          DEFAULT_DURATION_IN_MINUTES -
          (currentTime.minute() % DEFAULT_DURATION_IN_MINUTES);

        currentTime.add(remainder, 'minute');
      }

      return timeslots;
    },
    [dentistHoursByDay, durationInMinutes, requests]
  );

  const fetchSelectedDentistSchedule = useCallback(async () => {
    setLoading(true);
    setAvailableDates([]);
    setSelectedDate(null);
    try {
      if (!selectedDentist || !dentistHoursByDay) {
        return;
      }

      const today = moment();
      const maximumDate = moment()
        .add(MAXIMUM_DATE_RANGE_IN_MONTHS, 'months')
        .endOf('week');
      const dates: Date[] = [];

      const date = today.clone();
      while (date.isBefore(maximumDate)) {
        const timeslots = getTimeslotsForDate(date.toDate());
        if (timeslots.length > 0) {
          dates.push(date.toDate());
        }
        date.add(1, 'day');
        await new Promise((resolve) => setTimeout(resolve, 0));
      }

      setAvailableDates(dates);
      if (dates.length > 0) {
        setSelectedDate(dates[0]);
        setCurrentStep(SCHEDULE_FLOW_STEPS.SELECT_DATE);
      } else {
        displayToast({
          message: `${formatDentistName({
            dentist: selectedDentist,
            returnDentistFullName: true
          })} is unavailable. ${
            dentists.length > 1
              ? 'Please choose another dentist or contact us at '
              : 'For assistance, please contact us at '
          }${formatMobileNumber(clinic?.mobile_number)}.`,
          duration: 10000,
          position: 'top',
          positionAnchor: 'logo'
        });
        setCurrentStep(SCHEDULE_FLOW_STEPS.SELECT_DENTIST);
      }
    } catch (error: any) {
      displayToast({
        message: `Error fetching dentist schedule: ${error?.message}. Please contact support.`,
        duration: 5000,
        position: 'top',
        positionAnchor: 'logo'
      });
    } finally {
      setLoading(false);
    }
  }, [displayToast, selectedDentist?.id, selectedDate, getTimeslotsForDate]);

  const sendOtp = async () => {
    setLoading(true);
    try {
      if (!selectedDentist?.patient_verification_enabled) {
        const uid = generateUid();
        await createPatient({ ...patient, id: uid });
        setPatientId(uid);
        setCurrentStep(SCHEDULE_FLOW_STEPS.CONFIRMATION);
      } else {
        if (
          mobileNumber &&
          authenticated &&
          `63${mobileNumber}` === authenticatedMobileNumber &&
          authenticatedPatientId
        ) {
          /**
           * Update the main patient account with the provided patient details
           * and create a new and separate patient record to be linked to the current request
           **/
          const existingPatient = await getPatient(authenticatedPatientId);
          if (existingPatient) {
            await updatePatient({ patientId: authenticatedPatientId, patient });
          } else {
            await createPatient({
              ...patient,
              id: authenticatedPatientId,
              is_user: true
            });
          }

          const uid = generateUid();
          await createPatient({ ...patient, id: uid });

          setPatientId(uid);
          /** Skip verification step since user is already authenticated **/
          setCurrentStep(SCHEDULE_FLOW_STEPS.CONFIRMATION);
        } else {
          const { error } = await supabase.functions.invoke('otp', {
            body: {
              operation: 'send',
              payload: {
                phone_number: `63${mobileNumber}`
              }
            }
          });

          if (error) {
            displayToast({
              message: `Error sending OTP: ${error.message}. Please contact support.`,
              duration: 5000,
              position: 'top',
              positionAnchor: 'logo'
            });
          } else {
            setCurrentStep(SCHEDULE_FLOW_STEPS.VERIFICATION);
          }
        }
      }
    } catch (error: any) {
      displayToast({
        message: `Error ${
          selectedDentist?.patient_verification_enabled
            ? 'creating patient'
            : 'sending OTP'
        }: ${error.message}. Please contact support.`,
        duration: 5000,
        position: 'top',
        positionAnchor: 'logo'
      });
    }
    setLoading(false);
  };

  const resendOtp = async () => {
    setLoading(true);
    await supabase.functions.invoke('otp', {
      body: {
        operation: 'resend',
        payload: {
          phone_number: `63${mobileNumber}`
        }
      }
    });
    setLoading(false);
  };

  const verifyOtp = async (otp: string) => {
    setLoading(true);
    try {
      const {
        data: { session },
        error
      } = await supabase.auth.verifyOtp({
        phone: `63${mobileNumber}`,
        token: otp,
        type: 'sms'
      });

      if (error) {
        displayToast({
          message: `Error verifying OTP: ${error.message}. Please contact support.`,
          duration: 5000,
          position: 'top',
          positionAnchor: 'logo'
        });
        setLoading(false);
        return;
      }

      if (session && session.user.id && session.user.phone) {
        /**
         * Update the main patient account with the provided patient details
         * and create a new and separate patient record to be linked to the current request
         **/
        const existingPatient = await getPatient(session.user.id);
        if (existingPatient) {
          await updatePatient({ patientId: session.user.id, patient });
        } else {
          await createPatient({
            ...patient,
            id: session.user.id,
            is_user: true
          });
        }

        const uid = generateUid();
        await createPatient({ ...patient, id: uid });

        setPatientId(uid);
        setCurrentStep(SCHEDULE_FLOW_STEPS.CONFIRMATION);
        setLoading(false);
      }
    } catch (error: any) {
      displayToast({
        message: `Error verifying OTP: ${error.message}. Please contact support.`,
        duration: 5000,
        position: 'top',
        positionAnchor: 'logo'
      });
      setLoading(false);
    }
  };

  const confirmRequest = useCallback(async () => {
    setLoading(true);
    try {
      if (!selectedDentist?.id) {
        throw new Error('No dentist selected');
      }

      /**
       * Perform a final check if the selected timeslot is already taken
       **/
      const [approvedRequests, rescheduledRequests, pendingRequests] =
        await Promise.all([
          getRequestsByDentistIdAndStatus(
            selectedDentist?.id,
            REQUEST_STATUS_OPTIONS.APPROVED
          ),
          getRequestsByDentistIdAndStatus(
            selectedDentist?.id,
            REQUEST_STATUS_OPTIONS.RESCHEDULED
          ),
          getRequestsByDentistIdAndStatus(
            selectedDentist?.id,
            REQUEST_STATUS_OPTIONS.PENDING
          )
        ]);
      const existingRequests = [
        ...approvedRequests,
        ...rescheduledRequests,
        ...pendingRequests
      ];
      const conflictingRequests = existingRequests.filter((existingRequest) => {
        const requestStartTime = moment(
          `${request.date} ${request.start_time}`,
          'YYYY-MM-DD HH:mm'
        );
        const requestEndTime = moment(
          `${request.date} ${request.end_time}`,
          'YYYY-MM-DD HH:mm'
        );
        const existingStartTime = moment(
          `${existingRequest.date} ${existingRequest.start_time}`,
          'YYYY-MM-DD HH:mm'
        );
        const existingEndTime = moment(
          `${existingRequest.date} ${existingRequest.end_time}`,
          'YYYY-MM-DD HH:mm'
        );

        return (
          requestStartTime.isBetween(
            existingStartTime,
            existingEndTime,
            null,
            '[)'
          ) ||
          requestEndTime.isBetween(
            existingStartTime,
            existingEndTime,
            null,
            '(]'
          ) ||
          existingStartTime.isBetween(
            requestStartTime,
            requestEndTime,
            null,
            '[)'
          ) ||
          existingEndTime.isBetween(
            requestStartTime,
            requestEndTime,
            null,
            '(]'
          )
        );
      });
      if (conflictingRequests.length > 0) {
        displayToast({
          message:
            'The timeslot you selected has just been booked by another patient. Please choose a different timeslot.',
          duration: 8000,
          position: 'top',
          positionAnchor: 'logo'
        });
        setRequests(existingRequests);
        setCurrentStep(SCHEDULE_FLOW_STEPS.SELECT_DATE);
        setLoading(false);
        return;
      }

      /**
       * Create the request
       **/
      const newRequest = await createRequest(request);
      if (newRequest) {
        setCurrentStep(SCHEDULE_FLOW_STEPS.THANK_YOU);
      } else {
        displayToast({
          message: 'Error creating request. Please contact support.',
          duration: 5000,
          position: 'top',
          positionAnchor: 'logo'
        });
      }
    } catch (error: any) {
      displayToast({
        message: `Error creating request: ${error.message}. Please contact support.`,
        duration: 5000,
        position: 'top',
        positionAnchor: 'logo'
      });
    }
    setLoading(false);
  }, [request]);

  const reset = useCallback(async () => {
    setLoading(true);
    await supabase.auth.signOut();
    setCurrentStep(SCHEDULE_FLOW_STEPS.SELECT_DENTIST);
    setSelectedDentist(null);
    setAvailableDates([]);
    setAvailableTimeslots([]);
    setPatientId(null);
    setFirstName(null);
    setLastName(null);
    setMobileNumber(null);
    setGender(null);
    setBirthday(null);
    setAuthenticatedMobileNumber(null);
    setAuthenticatedPatientId(null);
    setAuthenticated(false);
    setSelectedDate(null);
    setPurpose(null);
    await fetchClinicAndDentists();
    setLoading(false);
  }, [fetchClinicAndDentists]);

  useEffect(() => {
    const fetchUser = async () => {
      const {
        data: { user }
      } = await supabase.auth.getUser();

      if (user) {
        const patient = await getPatient(user.id);
        setAuthenticated(true);
        setAuthenticatedMobileNumber(user.phone!);
        setAuthenticatedPatientId(user.id);
        if (patient) {
          setFirstName(patient.first_name);
          setLastName(patient.last_name);
          setMobileNumber(patient.mobile_number);
          setGender(patient.gender);
          setBirthday(patient.birthday);
        }
      }
    };

    fetchUser();
  }, []);

  useEffect(() => {
    fetchClinicAndDentists();
  }, []);

  useEffect(() => {
    const fetchDentistData = async () => {
      if (selectedDentist?.id) {
        const [
          dentistHoursData,
          approvedRequests,
          rescheduledRequests,
          pendingRequests
        ] = await Promise.all([
          getDentistHoursByDentistId(selectedDentist.id!),
          getRequestsByDentistIdAndStatus(
            selectedDentist.id,
            REQUEST_STATUS_OPTIONS.APPROVED
          ),
          getRequestsByDentistIdAndStatus(
            selectedDentist.id,
            REQUEST_STATUS_OPTIONS.RESCHEDULED
          ),
          getRequestsByDentistIdAndStatus(
            selectedDentist.id,
            REQUEST_STATUS_OPTIONS.PENDING
          )
        ]);

        const hours: { [key: number]: DentistHour | null } = {};
        dentistHoursData.forEach((dentistHour) => {
          hours[dentistHour.day] = dentistHour.open ? dentistHour : null;
        });

        setDentistHoursByDay(hours);

        const allRequests = [
          ...approvedRequests,
          ...rescheduledRequests,
          ...pendingRequests
        ];
        setRequests(allRequests);
      } else {
        setDentistHoursByDay(null);
        setRequests([]);
      }
    };

    fetchDentistData();
  }, [selectedDentist?.id]);

  useEffect(() => {
    if (selectedDate) {
      const timeslots = getTimeslotsForDate(selectedDate);
      setAvailableTimeslots(timeslots);
      setSelectedTime(timeslots[0]);
    } else {
      setAvailableTimeslots([]);
    }
  }, [selectedDate, getTimeslotsForDate]);

  useEffect(() => {
    if (posthog && currentStep) {
      posthog.capture('step', {
        step: currentStep
      });
    }
  }, [currentStep, posthog]);

  return (
    <ScheduleFlowContext.Provider
      value={{
        loading,
        clinic,
        dentists,
        selectedDentist,
        setSelectedDentist,
        availableDates,
        setAvailableDates,
        availableTimeslots,
        selectedDate,
        setSelectedDate,
        selectedTime,
        setSelectedTime,
        purpose,
        setPurpose,
        othersPurpose,
        setOthersPurpose,
        currentStep,
        setCurrentStep,
        patient,
        patientId,
        firstName,
        lastName,
        mobileNumber,
        gender,
        birthday,
        setFirstName,
        setLastName,
        setMobileNumber,
        setGender,
        setBirthday,
        durationInMinutes,
        services,
        fetchServices,
        fetchSelectedDentistSchedule,
        sendOtp,
        resendOtp,
        verifyOtp,
        request,
        confirmRequest,
        reset
      }}
    >
      {children}
    </ScheduleFlowContext.Provider>
  );
};

export const useScheduleFlow = () => {
  const context = useContext(ScheduleFlowContext);
  if (context === undefined) {
    throw new Error(
      'useScheduleFlow must be used within a ScheduleFlowProvider'
    );
  }
  return context;
};
